/**
 * NewsletterPromoPopup — the storefront's timed mailing-list popup.
 *
 * Appears `delaySeconds` after load, then suppresses itself for 30 days (per org,
 * via localStorage) once the visitor dismisses or submits — so it isn't naggy
 * across visits. Renders the shared NewsletterSignupBlock so the signup behaviour
 * matches the standalone /newsletter page exactly (DRY). Only ever rendered on the
 * public storefront (not in the editor) and only when the org enabled it.
 */

import { useEffect, useState } from 'react';
import { NewsletterSignupBlock } from './NewsletterSignupBlock';
import type { PopupConfig } from '@/services/pageBuilderService';

const SUPPRESS_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

const suppressKey = (orgSlug: string) => `sg_nl_popup_${orgSlug || 'default'}`;

function isSuppressed(orgSlug: string): boolean {
  try {
    const ts = Number(localStorage.getItem(suppressKey(orgSlug)));
    return Number.isFinite(ts) && ts > 0 && Date.now() - ts < SUPPRESS_MS;
  } catch {
    return false;
  }
}

function suppress(orgSlug: string) {
  try { localStorage.setItem(suppressKey(orgSlug), String(Date.now())); } catch { /* private mode */ }
}

export function NewsletterPromoPopup({ orgSlug, config }: { orgSlug: string; config: PopupConfig }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!config.enabled || isSuppressed(orgSlug)) return;
    const t = setTimeout(() => setOpen(true), Math.max(0, (config.delaySeconds || 0) * 1000));
    return () => clearTimeout(t);
  }, [orgSlug, config.enabled, config.delaySeconds]);

  if (!open) return null;

  // Dismissing (× or backdrop) and submitting both arm the 30-day suppression.
  const close = () => { suppress(orgSlug); setOpen(false); };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center bg-black/50 p-4 sm:items-center"
      onClick={close}
    >
      <div
        className="relative w-full max-w-sm rounded-xl border border-zinc-700 bg-zinc-950 p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={close}
          aria-label="Close"
          className="absolute right-3 top-3 text-lg leading-none text-zinc-500 hover:text-white"
        >
          ×
        </button>
        <NewsletterSignupBlock
          orgSlug={orgSlug}
          compact
          headline={config.headline}
          subtext={config.subtext}
          onSubmitted={() => suppress(orgSlug)}
        />
      </div>
    </div>
  );
}

export default NewsletterPromoPopup;
